import * as cornerstone from "@cornerstonejs/core";
import dicomImageLoader from "@cornerstonejs/dicom-image-loader";
import { calculateSUVScalingFactors } from "@cornerstonejs/calculate-suv";

// Cornerstone pre-scales every PT image by `suvbw` IF a `scalingModule` metadata
// provider returns one, after which ROI stats are in SUV and getPixelValueUnits()
// reports "SUV" instead of "BQML" (Rectangle/Ellipse/Circle/Freehand/Probe all flip).
// The app never registered that provider, so PET stayed raw BQML. This module
// computes suvbw from the PET DICOM tags (via @cornerstonejs/calculate-suv, which
// handles the decay-correction edge cases) and registers the provider.
// If the tags are missing/invalid, calculate-suv throws; we swallow it and return
// undefined so the loader falls back to raw BQML — no regression on non-PET data.
// Ported from task/core/pet-suv-roi-measurement (eede9c542, built on Cornerstone
// 2.17.6); the scalingModule → suvbw pre-scale path is unchanged in 4.18.4.

const stripScheme = (imageId) => {
  if (typeof imageId !== "string") return imageId;
  const colonIdx = imageId.indexOf(":");
  return colonIdx === -1 ? imageId : imageId.slice(colonIdx + 1);
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

// DICOM-JSON accessors (wadors metaDataManager): each tag is { vr, Value: [...] };
// a sequence item is itself a tag-keyed object.
const jsonValue = (dataset, tag, index = 0) => {
  const el = dataset?.[tag];
  if (!el || !Array.isArray(el.Value) || el.Value.length <= index) {
    return undefined;
  }
  return el.Value[index];
};
const jsonValues = (dataset, tag) => {
  const el = dataset?.[tag];
  return el && Array.isArray(el.Value) ? el.Value : undefined;
};

const readFromWadors = (imageId) => {
  const mgr = dicomImageLoader?.wadors?.metaDataManager;
  if (!mgr || typeof mgr.get !== "function") return null;
  let ds = mgr.get(imageId);
  if (!ds) {
    // Multi-frame frames are stored under the frame-1 imageId.
    const base = imageId.replace(/\/frames\/\d+$/, "/frames/1");
    if (base !== imageId) ds = mgr.get(base);
  }
  if (!ds) return null;

  const modality = jsonValue(ds, "00080060");
  if (modality !== "PT") return { modality };

  const radio = jsonValue(ds, "00540016"); // RadiopharmaceuticalInformationSequence item
  return {
    modality,
    CorrectedImage: jsonValues(ds, "00280051") || jsonValue(ds, "00280051"),
    Units: jsonValue(ds, "00541001"),
    DecayCorrection: jsonValue(ds, "00541102"),
    PatientWeight: toNum(jsonValue(ds, "00101030")),
    PatientSize: toNum(jsonValue(ds, "00101020")),
    PatientSex: jsonValue(ds, "00100040"),
    SeriesDate: jsonValue(ds, "00080021"),
    SeriesTime: jsonValue(ds, "00080031"),
    AcquisitionDate: jsonValue(ds, "00080022"),
    AcquisitionTime: jsonValue(ds, "00080032"),
    RadionuclideTotalDose: radio ? toNum(jsonValue(radio, "00181074")) : undefined,
    RadionuclideHalfLife: radio ? toNum(jsonValue(radio, "00181075")) : undefined,
    RadiopharmaceuticalStartTime: radio ? jsonValue(radio, "00181072") : undefined,
    RadiopharmaceuticalStartDateTime: radio ? jsonValue(radio, "00181078") : undefined,
  };
};

// Fallback for the wadouri (dicom-parser) loader.
const readFromWadouri = (imageId) => {
  const cache = dicomImageLoader?.wadouri?.dataSetCacheManager;
  if (!cache || typeof cache.get !== "function") return null;
  // Cached under the URL without the ?frame=N hint (appended last for multi-frame).
  const uri = stripScheme(imageId).replace(/[?&]frame=\d+/i, "");
  const ds = cache.get(uri);
  if (!ds || typeof ds.string !== "function") return null;

  const modality = ds.string("x00080060");
  if (modality !== "PT") return { modality };

  const item = ds.elements?.["x00540016"]?.items?.[0]?.dataSet;
  return {
    modality,
    CorrectedImage: ds.string("x00280051"), // "ATTN\\DECY\\..." — String.includes works
    Units: ds.string("x00541001"),
    DecayCorrection: ds.string("x00541102"),
    PatientWeight: toNum(ds.floatString("x00101030")),
    PatientSize: toNum(ds.floatString("x00101020")),
    PatientSex: ds.string("x00100040"),
    SeriesDate: ds.string("x00080021"),
    SeriesTime: ds.string("x00080031"),
    AcquisitionDate: ds.string("x00080022"),
    AcquisitionTime: ds.string("x00080032"),
    RadionuclideTotalDose: item ? toNum(item.floatString("x00181074")) : undefined,
    RadionuclideHalfLife: item ? toNum(item.floatString("x00181075")) : undefined,
    RadiopharmaceuticalStartTime: item ? item.string("x00181072") : undefined,
    RadiopharmaceuticalStartDateTime: item ? item.string("x00181078") : undefined,
  };
};

// suvbw is series-level (constant across slices), so cache the result per imageId.
const factorsByImageId = new Map();
// Dedupe fallback warnings so a hundreds-of-slices series doesn't spam the console.
const loggedMessages = new Set();

const logOnce = (key, fn) => {
  if (loggedMessages.has(key)) return;
  loggedMessages.add(key);
  fn();
};

// Returns { factors, settled }. settled=false means the instance's metadata isn't
// in either loader cache yet, so the answer may change once it loads.
const computeFactors = (imageId) => {
  let meta = readFromWadors(imageId);
  if (!meta) meta = readFromWadouri(imageId);
  if (!meta) return { factors: null, settled: false };
  if (meta.modality !== "PT") return { factors: null, settled: true };

  try {
    const [factors] = calculateSUVScalingFactors([meta]);
    if (!factors || typeof factors.suvbw !== "number" || !Number.isFinite(factors.suvbw)) {
      logOnce("no-suvbw", () =>
        console.warn("::SUV:: calculateSUVScalingFactors returned no usable suvbw; using raw BQML")
      );
      return { factors: null, settled: true };
    }
    logOnce("ok", () =>
      console.warn("::SUV:: PET pre-scaled to SUVbw, factor =", factors.suvbw)
    );
    return { factors, settled: true };
  } catch (err) {
    logOnce("compute-error:" + (err?.message || "unknown"), () =>
      console.warn("::SUV:: cannot compute SUV, falling back to raw BQML:", err?.message)
    );
    return { factors: null, settled: true };
  }
};

export const getSuvScalingFactors = (imageId) => {
  if (typeof imageId !== "string") return null;
  if (factorsByImageId.has(imageId)) return factorsByImageId.get(imageId);
  const { factors, settled } = computeFactors(imageId);
  // OLD: cached every result, including a miss from asking before the metadata
  //      arrived — that pinned the image to BQML for the rest of the session.
  // NEW: cache only once the instance has actually been read.
  if (settled) factorsByImageId.set(imageId, factors);
  return factors;
};

const ptScalingMetadataProvider = (type, imageId) => {
  if (type !== "scalingModule") return undefined;
  const factors = getSuvScalingFactors(imageId);
  if (!factors) return undefined;
  const out = { suvbw: factors.suvbw };
  if (typeof factors.suvlbm === "number") out.suvlbm = factors.suvlbm;
  if (typeof factors.suvbsa === "number") out.suvbsa = factors.suvbsa;
  return out;
};

let registered = false;
export const registerPTSuvScalingProvider = () => {
  if (registered) return;
  // High priority; returns undefined for non-PT / uncomputable images so other
  // providers still get a turn.
  cornerstone.metaData.addProvider(ptScalingMetadataProvider, 10000);
  registered = true;
};

export const clearSuvScalingCache = () => {
  factorsByImageId.clear();
  loggedMessages.clear();
};
